import { Star, MapPin, Clock, Shield, Zap, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface Service {
  name: string;
  price: string;
  description: string;
}

interface ServiceCardProps {
  company: {
    id: string;
    name: string;
    image: string;
    rating: number;
    reviewCount: number;
    location: string;
    services: Service[];
    badges: string[];
    availability: string;
    activeOfferName?: string;
    activeDiscountPercent?: number;
  };
  onSelect: (companyId: string) => void;
}

const badgeIcons = {
  "Top Rated": Star,
  "Fast Availability": Zap,
  "Eco-Friendly": Leaf,
  "Verified": Shield,
};

export const ServiceCard = ({ company, onSelect }: ServiceCardProps) => {
  return (
    <motion.div
      className="h-full"
      whileHover={{ y: -8 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="h-full flex flex-col overflow-hidden hover:shadow-strong transition-all duration-300 transform bg-gradient-card border-0 cursor-pointer">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <motion.img
              src={company.image || "/assets/placeholders/logo-placeholder.svg"}
              alt={company.name}
              className="w-16 h-16 rounded-xl object-cover shadow-medium"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            />
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg text-foreground">{company.name}</h3>
                  <div className="flex items-center gap-1 mt-1">
                    <Star className="h-4 w-4 fill-accent text-accent" />
                    <span className="text-sm font-medium text-foreground">{company.rating}</span>
                    <span className="text-sm text-muted-foreground">({company.reviewCount} reviews)</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{company.location}</span>
                    {/* Local SEO badge */}
                    {company.location && (
                      <Badge variant="outline" className="text-xs ml-1 border-primary/30 text-primary">
                        Disponibile a {company.location}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Badges */}
          <div className="flex flex-wrap gap-1 mt-3">
            {company.activeOfferName && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.05 }}
              >
                <Badge variant="default" className="text-xs bg-primary text-white">
                  {company.activeOfferName} • -{company.activeDiscountPercent}%
                </Badge>
              </motion.div>
            )}
            {company.badges.map((badge, index) => {
              const IconComponent = badgeIcons[badge as keyof typeof badgeIcons] || Shield;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Badge variant="secondary" className="text-xs">
                    <IconComponent className="h-3 w-3 mr-1" />
                    {badge}
                  </Badge>
                </motion.div>
              );
            })}
          </div>
        </CardHeader>

        <CardContent className="py-3 flex-1">
          {/* Services */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-foreground">Services & Pricing</h4>
            {company.services.slice(0, 3).map((service, index) => (
              <motion.div 
                key={index} 
                className="flex justify-between items-start"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{service.name}</p>
                  <p className="text-xs text-muted-foreground">{service.description}</p>
                </div>
                <span className="text-xs font-semibold text-primary ml-2 bg-primary/10 text-primary px-2 py-1 rounded-md">
                  {service.price}
                </span>
              </motion.div>
            ))}
            {company.services.length > 3 && (
              <p className="text-xs text-muted-foreground">+{company.services.length - 3} more services</p>
            )}
          </div>

          {/* Availability */}
          <motion.div 
            className="flex items-center gap-2 mt-4 p-2 bg-success/10 rounded-lg"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Clock className="h-4 w-4 text-success" />
            <span className="text-sm text-success font-medium">{company.availability}</span>
          </motion.div>
        </CardContent>

      <CardFooter className="pt-3 mt-auto">
        <Button 
          onClick={() => onSelect(company.id)}
          className="w-full"
          variant="hero"
        >
          View Details & Book
        </Button>
      </CardFooter>
    </Card>
    </motion.div>
  );
};